import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	GitBranch,
	MessageSquare,
	Server,
	Workflow,
} from "lucide-react";
import {
	type ComparisonRow,
	ComparisonTable,
} from "@/components/comparison-table";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { PromptCalendarExample } from "@/components/prompt-calendar-example";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/alternatives/postiz";

export const Route = createFileRoute(path)({
	component: PostizAlternative,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | Postiz Alternative for AI Social Media Scheduling",
			description:
				"Looking for a Postiz alternative? AgentReacher is the agent-native social media scheduler for teams that want Claude, ChatGPT, and MCP to draft, adapt, schedule, and publish.",
		}),
});

const comparison: ComparisonRow[] = [
	{
		feature: "AI agent workflow",
		competitor: "In-app AI",
		agentreacher: "Native",
	},
	{ feature: "MCP integration", competitor: false, agentreacher: true },
	{
		feature: "Chat-based scheduling",
		competitor: "Dashboard-first",
		agentreacher: true,
	},
	{ feature: "Self-hosting", competitor: true, agentreacher: false },
	{ feature: "Cross-posting", competitor: true, agentreacher: true },
	{
		feature: "Per-platform variations",
		competitor: "Manual / assisted",
		agentreacher: "Auto",
	},
	{ feature: "Bluesky and Threads", competitor: true, agentreacher: true },
	{ feature: "Approval flow", competitor: true, agentreacher: "Built in" },
];

function PostizAlternative() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Postiz alternative"
				title="A Postiz alternative for AI social media agents."
				description="Postiz is a strong open-source scheduler. AgentReacher is for teams that want the workflow to start in Claude, ChatGPT, Cursor, or any MCP client: prompt, draft, adapt, approve, schedule."
			/>

			<MarketingSection
				title="Why teams compare Postiz and AgentReacher"
				description="Both tools help you publish across social channels. The difference is where the work happens: Postiz is calendar-first, AgentReacher is agent-first."
			>
				<FeatureGrid
					features={[
						{
							icon: Bot,
							title: "Agent-native workflow",
							description:
								"Ask your assistant for a week of posts, review the queue, and publish without rebuilding every caption by hand.",
						},
						{
							icon: Workflow,
							title: "MCP for AI assistants",
							description:
								"Connect Claude, ChatGPT, Cursor, or another MCP client to schedule and publish through AgentReacher.",
						},
						{
							icon: MessageSquare,
							title: "From prompt to calendar",
							description:
								"Describe the campaign in plain English. The agent turns it into channel-specific posts and time slots.",
						},
						{
							icon: GitBranch,
							title: "Platform-aware variations",
							description:
								"Turn one idea into posts for LinkedIn, X, Instagram, Threads, Bluesky, TikTok, YouTube, Pinterest, and Facebook.",
						},
						{
							icon: CalendarClock,
							title: "Calendar when you need it",
							description:
								"Use the calendar for review and approvals, not as the place where every post has to be created manually.",
						},
						{
							icon: Server,
							title: "Managed instead of self-hosted",
							description:
								"Choose AgentReacher when you want a hosted AI scheduling workflow instead of running and maintaining your own scheduler.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="AgentReacher vs. Postiz">
				<ComparisonTable competitorName="Postiz" rows={comparison} />
			</MarketingSection>

			<MarketingSection
				title="Live prompt-to-calendar example"
				description="This is the practical difference: instead of opening a scheduler and filling empty slots, you ask your agent for the campaign and review the populated calendar."
			>
				<PromptCalendarExample
					assistant="AI social media agent"
					prompt="Plan next week's launch posts from this product update. Make LinkedIn technical, X concise, and Threads conversational. Schedule the whole sequence."
					response="I drafted channel-specific versions, spaced them across the week, and queued each post for approval in the calendar."
					slots={[
						{
							day: "Monday",
							time: "09:00",
							channel: "LinkedIn",
							title:
								"Technical launch post explaining the workflow problem and new capability.",
						},
						{
							day: "Wednesday",
							time: "11:00",
							channel: "X",
							title:
								"Concise launch thread with product screenshots and setup CTA.",
						},
						{
							day: "Friday",
							time: "14:30",
							channel: "Threads",
							title:
								"Conversational recap asking how teams currently schedule content.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="Which one should you choose?">
				<div className="mx-auto max-w-2xl space-y-4 text-sm leading-relaxed text-muted-foreground">
					<p>
						Choose Postiz if you specifically want an open-source, self-hosted
						social media scheduler and are comfortable managing the setup. It is
						a good fit for teams that want direct control over the
						infrastructure and a familiar calendar-first publishing workflow.
					</p>
					<p>
						Choose AgentReacher if you want your AI assistant to do more of the
						work: draft the campaign, adapt posts per network, schedule the
						queue, and keep publishing from chat. You still get a calendar, but
						the blank page is gone because the workflow starts with the agent.
					</p>
					<p>
						Compare AgentReacher with{" "}
						<Link
							to="/alternatives/buffer"
							className="text-primary transition-colors hover:text-primary/80"
						>
							Buffer
						</Link>{" "}
						and{" "}
						<Link
							to="/alternatives/hootsuite"
							className="text-primary transition-colors hover:text-primary/80"
						>
							Hootsuite
						</Link>
						.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
